import type { FC } from 'react';
import {
  BenchlingBackfillEntityType,
  SyncEntityAnimalDetailsV1,
  SyncEntityDosageDetailsV1,
  SyncEntityMeasurementDetailsV1,
  SyncEntityObservationDetailsV1,
  SyncEntitySampleDetailsV1,
  SyncEntityStudyDetailsV1,
  SyncEntityStudyGroupDetailsV1,
  SyncEntityTreatmentDetailsV1,
} from '@/model/BenchlingBackfill.model';
import { DateUtils } from '@/utils/Date.utils';
import { copyText } from '@/helpers.tsx';
import { RiFileCopyLine } from 'react-icons/ri';

// Common property display component
export const SyncEntityPropRow: FC<{
  label: string;
  value: string | number | null | undefined;
  type?: 'text' | 'date' | 'datetime' | 'apiId';
}> = ({ label, value, type = 'text' }) => {
  const formatValue = () => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    switch (type) {
      case 'date':
        return DateUtils.renderDate(String(value));
      case 'datetime':
        return DateUtils.renderDateTime(String(value));
      default:
        return String(value);
    }
  };

  return (
    <div className="flex mb2">
      <div className="w4 fw5">{label}</div>
      <div>{formatValue()}</div>
      {type === 'apiId' && (
        <a className="ml1 pointer" onClick={() => copyText(String(value), 'Copied to clipboard')}>
          <RiFileCopyLine />
        </a>
      )}
    </div>
  );
};

// Section wrapper for grouping properties
export const SyncEntityPropSection: FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="">
    <h4 className="fw5 mb2 pt3">{title}</h4>
    {children}
  </div>
);

// Entity-specific display components
export const AnimalDisplay: FC<{ entity: SyncEntityAnimalDetailsV1 }> = ({ entity }) => (
  <>
    <SyncEntityPropRow label="Name" value={entity.name} />
    <SyncEntityPropRow label="Catalog ID" value={entity.catalog_id} />
    <SyncEntityPropRow label="Api ID" value={entity.api_id} type="apiId" />
  </>
);

export const StudyDisplay: FC<{ entity: SyncEntityStudyDetailsV1 }> = ({ entity }) => (
  <>
    <SyncEntityPropRow label="Name" value={entity.name} />
    <SyncEntityPropRow label="Code" value={entity.code} />
    <SyncEntityPropRow label="Api ID" value={entity.api_id} type="apiId" />
  </>
);

export const TreatmentDisplay: FC<{ entity: SyncEntityTreatmentDetailsV1 }> = ({ entity }) => (
  <>
    <SyncEntityPropRow label="Treatment" value={entity.name} />
    <SyncEntityPropRow label="Api ID" value={entity.api_id} type="apiId" />
  </>
);

export const DosageDisplay: FC<{ entity: SyncEntityDosageDetailsV1 }> = ({ entity }) => (
  <>
    <SyncEntityPropRow label="Dosed at" value={entity.dosed_at} type="datetime" />
    <SyncEntityPropRow label="Treatment" value={entity.treatment.name} />
    <SyncEntityPropRow label="Api ID" value={entity.api_id} type="apiId" />

    {entity.animal && (
      <SyncEntityPropSection title="Animal">
        <SyncEntityPropRow label="Name" value={entity.animal.name} />
        <SyncEntityPropRow label="Catalog ID" value={entity.animal.catalog_id} />
        <SyncEntityPropRow label="Api ID" value={entity.animal.api_id} type="apiId" />
      </SyncEntityPropSection>
    )}
  </>
);

export const SampleDisplay: FC<{ entity: SyncEntitySampleDetailsV1 }> = ({ entity }) => (
  <>
    <SyncEntityPropRow label="Sample ID" value={entity.sample_id} />
    <SyncEntityPropRow label="Collected at" value={entity.collected_at} type="datetime" />
    <SyncEntityPropRow label="Api ID" value={entity.api_id} type="apiId" />

    {entity.animal && (
      <SyncEntityPropSection title="Animal">
        <SyncEntityPropRow label="Name" value={entity.animal.name} />
        <SyncEntityPropRow label="Catalog ID" value={entity.animal.catalog_id} />
        <SyncEntityPropRow label="Api ID" value={entity.animal.api_id} type="apiId" />
      </SyncEntityPropSection>
    )}
  </>
);

export const MeasurementDisplay: FC<{ entity: SyncEntityMeasurementDetailsV1 }> = ({ entity }) => (
  <>
    <SyncEntityPropRow label="Measured at" value={entity.measured_at} type="date" />
    <SyncEntityPropRow label="Api ID" value={entity.api_id} type="apiId" />

    {entity.animal && (
      <SyncEntityPropSection title="Animal">
        <SyncEntityPropRow label="Animal Name" value={entity.animal.name} />
        <SyncEntityPropRow label="Catalog ID" value={entity.animal.catalog_id} />
        <SyncEntityPropRow label="Api ID" value={entity.animal.api_id} type="apiId" />
      </SyncEntityPropSection>
    )}
  </>
);

export const ObservationDisplay: FC<{ entity: SyncEntityObservationDetailsV1 }> = ({ entity }) => (
  <>
    <SyncEntityPropRow label="Type" value={entity.observation_type} />
    <SyncEntityPropRow label="Observation at" value={entity.observed_at} type="datetime" />
    <SyncEntityPropRow label="Api ID" value={entity.api_id} type="apiId" />

    {entity.animal && (
      <SyncEntityPropSection title="Animal">
        <SyncEntityPropRow label="Animal Name" value={entity.animal.name} />
        <SyncEntityPropRow label="Catalog ID" value={entity.animal.catalog_id} />
        <SyncEntityPropRow label="Api ID" value={entity.animal.api_id} type="apiId" />
      </SyncEntityPropSection>
    )}
  </>
);

export const StudyGroupDisplay: FC<{ entity: SyncEntityStudyGroupDetailsV1 }> = ({ entity }) => (
  <>
    <SyncEntityPropRow label="Name" value={entity.name} />
    <SyncEntityPropRow label="Api ID" value={entity.api_id} type="apiId" />
  </>
);

// Component mapping
export const SYNC_ENTITY_DISPLAY_COMPONENTS: Record<BenchlingBackfillEntityType, FC<{ entity: any }>> = {
  animals: AnimalDisplay,
  studies: StudyDisplay,
  treatments: TreatmentDisplay,
  dosages: DosageDisplay,
  samples: SampleDisplay,
  measurements: MeasurementDisplay,
  observations: ObservationDisplay,
  study_groups: StudyGroupDisplay,
};
